// PEDIDOS DE CANCELAMENTO do servidor sobre consignacoes facultativas (#276).
//
// O servidor pede pelo app; a averbadora acompanha aqui, por orgao/entidade,
// com o prazo de resposta. Responder REGISTRA o que a consignataria fez — nao
// encerra o desconto. Quem encerra e' a consignataria, pelo arquivo (ou #281).
// A tela diz isso no modal, para ninguem marcar "atendida" achando que parou a
// cobranca.

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  DataTable,
  FilterChips,
  Modal,
  NumberField,
  Pagination,
  Pill,
  SelectField,
  TextareaField,
  fmtBRL,
  type Column,
} from "@atlas/ui/web";
import type { AssuntoAlteracaoConsignacao, SolicitacaoConsignacaoNaFila, StatusSolicitacaoConsignacao } from "@atlas/sdk";

const ROTULO_ASSUNTO: Record<AssuntoAlteracaoConsignacao, string> = { mudar_plano: "Mudar de plano", dependentes: "Incluir ou excluir dependente", outro: "Outro assunto" };
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { useFiltros } from "../../lib/use-filtros";
import { EncerrarConsignacaoModal } from "./consignatarias-consignacoes";
import { AutorizacaoDoOrgao } from "./autorizacao-config";

const fmtData = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

const ROTULO_STATUS: Record<StatusSolicitacaoConsignacao, string> = {
  aberta: "Aberto",
  atendida: "Atendido",
  recusada: "Recusado",
};

function quemPresta(p: SolicitacaoConsignacaoNaFila): string {
  if (p.credorFinalNome && p.consignatariaNome) return `${p.credorFinalNome}, via ${p.consignatariaNome}`;
  return p.credorFinalNome ?? p.consignatariaNome ?? "—";
}

export function AdminSolicitacoesCancelamento() {
  const qc = useQueryClient();
  const [unidadeId, setUnidadeId] = useState<number | null>(null);
  const [status, setStatus] = useState<"" | StatusSolicitacaoConsignacao>("aberta");
  const [pagina, setPagina] = useState(1);
  const [respondendo, setRespondendo] = useState<SolicitacaoConsignacaoNaFila | null>(null);
  // #281: encerrar a consignacao e atender o pedido num passo so'.
  const [encerrando, setEncerrando] = useState<SolicitacaoConsignacaoNaFila | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // O orgao e' contexto (sem ele nao ha fila), e nao filtro; o filtro e' a
  // situacao, com default "aberta" — o que a averbadora tem a responder.
  const filtros = useFiltros(
    [
      { contexto: true, ligado: unidadeId !== null, limpar: () => setUnidadeId(null) },
      { nome: "status", ligado: status !== "aberta", limpar: () => setStatus("aberta") },
    ],
    setPagina,
  );

  const orgaosQ = useQuery({
    queryKey: ["admin", "prefeituras", "solicitacoes-cancelamento"],
    queryFn: () => atlas.admin.listPrefeituras(),
  });
  const opcoesOrgao = useMemo(
    () => (orgaosQ.data?.prefeituras ?? []).map((p) => ({ value: String(p.id), label: p.nome })),
    [orgaosQ.data],
  );

  const filaQ = useQuery({
    queryKey: ["admin", "consignacao-solicitacoes", unidadeId, status, pagina],
    queryFn: () =>
      atlas.admin.consignacaoSolicitacoes.listar({
        unidadeId: unidadeId!,
        ...(status ? { status } : {}),
        pagina,
      }),
    enabled: unidadeId !== null,
  });

  const colunas: Column<SolicitacaoConsignacaoNaFila>[] = [
    {
      key: "tipo",
      header: "Pedido",
      wrap: true,
      render: (p) => (
        <div>
          <div style={{ fontWeight: 600 }}>{p.tipo === "alteracao" ? "Alteração" : "Cancelamento"}</div>
          {p.assunto ? <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{ROTULO_ASSUNTO[p.assunto]}</div> : null}
        </div>
      ),
    },
    {
      key: "servidor",
      header: "Servidor",
      wrap: true,
      render: (p) => (
        <div>
          <div style={{ fontWeight: 600 }}>{p.servidor.nome ?? "—"}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            mat. {p.servidor.matricula} · {p.servidor.cpfMasked}
          </div>
        </div>
      ),
    },
    {
      key: "produto",
      header: "Desconto",
      wrap: true,
      render: (p) => (
        <div>
          <div>{p.rotulo}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {quemPresta(p)} · rubrica {p.rubrica}
          </div>
        </div>
      ),
    },
    {
      key: "valor",
      header: "Valor mensal",
      align: "right",
      render: (p) => (p.valorVigenteCentavos == null ? "—" : fmtBRL(p.valorVigenteCentavos / 100)),
    },
    // #318: quem responde é a consignatária dona da rubrica; a averbadora supervisiona.
    {
      key: "responsavel",
      header: "Responsável",
      wrap: true,
      render: (p) => p.consignatariaNome ?? <span style={{ color: "var(--text-dim)" }}>Averbadora</span>,
    },
    { key: "pedido", header: "Pedido em", render: (p) => fmtData(p.solicitadaEm) },
    {
      key: "prazo",
      header: "Responder até",
      render: (p) =>
        p.vencida ? (
          <span style={{ color: "var(--danger, #DC2626)", fontWeight: 700 }}>{fmtData(p.responderAte)} · vencido</span>
        ) : (
          fmtData(p.responderAte)
        ),
    },
    {
      key: "status",
      header: "Situação",
      render: (p) => (
        <Pill variant={p.status === "aberta" ? (p.vencida ? "rejeitada" : "pendente") : p.status === "atendida" ? "aceita" : "expirado"}>
          {ROTULO_STATUS[p.status]}
        </Pill>
      ),
    },
    {
      key: "motivo",
      header: "Motivo / resposta",
      wrap: true,
      render: (p) => (
        <div style={{ fontSize: 13 }}>
          {p.motivo ? <div>“{p.motivo}”</div> : <div style={{ color: "var(--text-muted)" }}>sem motivo</div>}
          {p.resposta ? (
            <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
              Resposta ({fmtData(p.respondidaEm)}): {p.resposta}
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <header>
        <h1 style={{ margin: 0 }}>Pedidos de cancelamento e alteração</h1>
        <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 14 }}>
          Planos, seguros e mensalidades que o servidor pediu para cancelar ou alterar. Quem responde é a
          consignatária, no portal dela; aqui a averbadora acompanha e pode responder por ela. Responder registra o
          que foi feito — o desconto só muda quando a consignatária o encerra ou altera.
        </p>
      </header>

      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, alignItems: "end" }}>
          <SelectField
            label="Órgão/entidade"
            value={unidadeId === null ? "" : String(unidadeId)}
            onChange={(e) => {
              setUnidadeId(e.target.value ? Number(e.target.value) : null);
              setPagina(1);
            }}
            options={[{ value: "", label: "Escolha o órgão/entidade" }, ...opcoesOrgao]}
          />
          <FilterChips
            label="Situação"
            variant="segmentado"
            value={status}
            onChange={filtros.filtrar((v: string) => setStatus(v as "" | StatusSolicitacaoConsignacao))}
            options={[
              { value: "aberta", label: "Abertos" },
              { value: "atendida", label: "Atendidos" },
              { value: "recusada", label: "Recusados" },
              { value: "", label: "Todos" },
            ]}
          />
        </div>
      </Card>

      {unidadeId === null ? (
        <Card>
          <p style={{ margin: 0, color: "var(--text-muted)" }}>Escolha o órgão/entidade para ver os pedidos.</p>
        </Card>
      ) : (
        <>
          <PrazoDoOrgao unidadeId={unidadeId} />
          {/* #282: a exigencia de autorizacao do servidor neste orgao. */}
          <AutorizacaoDoOrgao unidadeId={unidadeId} />
          <Card>
            {filaQ.isError ? (
              <div role="alert">{mensagemDeErro(filaQ.error, "Não foi possível carregar os pedidos.")}</div>
            ) : (
              <>
                <DataTable
                  caption="Pedidos de cancelamento do órgão/entidade"
                  columns={colunas}
                  rows={filaQ.data?.itens ?? []}
                  rowKey={(p) => String(p.id)}
                  loading={filaQ.isLoading}
                  emptyState="Nenhum pedido nesta situação."
                  actions={(p) =>
                    p.status === "aberta" ? (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {p.consignatariaId != null ? (
                          <Button size="sm" variant="ghost" onClick={() => setEncerrando(p)}>
                            Encerrar e atender
                          </Button>
                        ) : null}
                        <Button size="sm" variant="ghost" onClick={() => setRespondendo(p)}>
                          Responder
                        </Button>
                      </div>
                    ) : null
                  }
                />
                {filaQ.data && filaQ.data.total > filaQ.data.porPagina ? (
                  <Pagination
                    total={filaQ.data.total}
                    pagina={pagina}
                    porPagina={filaQ.data.porPagina}
                    onPagina={setPagina}
                    onPorPagina={() => undefined}
                    opcoes={[filaQ.data.porPagina]}
                    rotulo="pedidos"
                  />
                ) : null}
              </>
            )}
          </Card>
        </>
      )}

      {aviso ? <p role="status">{aviso}</p> : null}
      {encerrando && encerrando.consignatariaId != null ? (
        <EncerrarConsignacaoModal
          credorId={encerrando.consignatariaId}
          consignacaoId={encerrando.consignacaoId}
          descricao={`${encerrando.rotulo} — matrícula ${encerrando.servidor.matricula}`}
          solicitacaoId={encerrando.id}
          onFechar={() => setEncerrando(null)}
          onFeito={(a) => {
            setAviso(a);
            void qc.invalidateQueries({ queryKey: ["admin", "consignacao-solicitacoes"] });
          }}
        />
      ) : null}
      {respondendo ? (
        <ResponderModal
          pedido={respondendo}
          onClose={() => setRespondendo(null)}
          onRespondido={async () => {
            setRespondendo(null);
            await qc.invalidateQueries({ queryKey: ["admin", "consignacao-solicitacoes"] });
          }}
        />
      ) : null}
    </div>
  );
}

function ResponderModal({
  pedido,
  onClose,
  onRespondido,
}: {
  pedido: SolicitacaoConsignacaoNaFila;
  onClose: () => void;
  onRespondido: () => void | Promise<void>;
}) {
  const [decisao, setDecisao] = useState<"atendida" | "recusada">("atendida");
  const [resposta, setResposta] = useState("");
  const [erro, setErro] = useState<unknown>(null);
  const responder = useMutation({
    mutationFn: () => atlas.admin.consignacaoSolicitacoes.responder(pedido.id, { status: decisao, resposta: resposta.trim() }),
    onSuccess: onRespondido,
    onError: setErro,
  });
  const curta = decisao === "recusada" && resposta.trim().length < 10;
  return (
    <Modal
      open
      onClose={onClose}
      title="Responder pedido de cancelamento"
      subtitle={`${pedido.servidor.nome ?? "Servidor"} · ${pedido.rotulo} — ${quemPresta(pedido)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={responder.isPending}>
            Voltar
          </Button>
          <Button onClick={() => responder.mutate()} disabled={responder.isPending || !resposta.trim() || curta}>
            {responder.isPending ? "Salvando…" : "Registrar resposta"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {pedido.consignatariaNome ? (
          <p role="note" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
            Quem responde a este pedido é <strong>{pedido.consignatariaNome}</strong>, pelo portal dela. Você está
            respondendo em nome dela — a auditoria registra.
          </p>
        ) : null}
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
          Registrar a resposta <strong>não encerra o desconto</strong>: ele só para quando a consignatária o encerra
          pelo arquivo.
        </p>
        <FilterChips
          label="Resposta"
          variant="segmentado"
          value={decisao}
          onChange={(v) => setDecisao(v as "atendida" | "recusada")}
          options={[
            { value: "atendida", label: "Atendido" },
            { value: "recusada", label: "Recusado" },
          ]}
        />
        <TextareaField
          label={decisao === "recusada" ? "Motivo da recusa" : "Resposta"}
          hint={decisao === "recusada" ? "Mínimo de 10 caracteres." : "Ex.: a consignatária confirmou o cancelamento a partir da próxima folha."}
          value={resposta}
          maxLength={1000}
          rows={3}
          onChange={(e) => setResposta(e.target.value)}
        />
        {erro ? (
          <div role="alert" style={{ fontSize: 13, color: "var(--danger, #DC2626)" }}>
            {mensagemDeErro(erro, "Não foi possível registrar a resposta.")}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function PrazoDoOrgao({ unidadeId }: { unidadeId: number }) {
  const qc = useQueryClient();
  const prazoQ = useQuery({
    queryKey: ["admin", "consignacao-solicitacoes", "prazo", unidadeId],
    queryFn: () => atlas.admin.consignacaoSolicitacoes.prazo(unidadeId),
  });
  const [editando, setEditando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const salvar = useMutation({
    mutationFn: (dias: number) => atlas.admin.consignacaoSolicitacoes.definirPrazo(unidadeId, dias),
    onSuccess: async () => {
      setEditando(null);
      setErro(null);
      await qc.invalidateQueries({ queryKey: ["admin", "consignacao-solicitacoes", "prazo", unidadeId] });
    },
    onError: setErro,
  });
  const p = prazoQ.data;
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700 }}>Prazo de resposta deste órgão/entidade</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
            {p
              ? `${p.prazoRespostaDias} dias corridos${p.padrao ? " (padrão)" : ""}. Vale para os pedidos novos; os abertos mantêm o prazo com que nasceram.`
              : "Carregando…"}
          </div>
        </div>
        {editando === null ? (
          <Button size="sm" variant="ghost" onClick={() => setEditando(String(p?.prazoRespostaDias ?? 30))} disabled={!p}>
            Alterar prazo
          </Button>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <NumberField
              label="Dias (1 a 90)"
              value={editando}
              min={1}
              max={90}
              onChange={(e) => setEditando(e.target.value)}
            />
            <Button size="sm" onClick={() => salvar.mutate(Number(editando))} disabled={salvar.isPending}>
              Salvar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditando(null)} disabled={salvar.isPending}>
              Cancelar
            </Button>
          </div>
        )}
      </div>
      {erro ? (
        <div role="alert" style={{ fontSize: 13, color: "var(--danger, #DC2626)", marginTop: 8 }}>
          {mensagemDeErro(erro, "Não foi possível salvar o prazo.")}
        </div>
      ) : null}
    </Card>
  );
}
